/**
 * BatchesTab — list, create, edit, delete batches.
 */
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2, Pencil, Boxes, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';

const NO_DEPT = '__none__';
const ALL_DEPTS = '__all__';

const EMPTY = {
  name: '', code: '', description: '', department_id: '',
  start_date: '', end_date: '', status: 'active', notes: '',
};

export default function BatchesTab() {
  const { get, post, put, del } = useApi();
  const { hasPermission } = useAuth();
  const canCreate = hasPermission('batches.create');
  const canEdit = hasPermission('batches.edit');
  const canDelete = hasPermission('batches.delete');
  const navigate = useNavigate();

  const [batches, setBatches] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [filterDept, setFilterDept] = useState(ALL_DEPTS);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [bs, ds] = await Promise.all([
        get('/trainees/batches'),
        get('/trainees/departments?include_inactive=true'),
      ]);
      setBatches(bs.batches || []);
      setDepartments(ds.departments || []);
    } catch { toast.error('Failed to load batches'); }
    finally { setLoading(false); }
  }, [get]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpenDialog(true); };
  const openEdit = (b) => {
    setEditing(b);
    setForm({
      name: b.name || '', code: b.code || '', description: b.description || '',
      department_id: b.department_id || '',
      start_date: b.start_date || '', end_date: b.end_date || '',
      status: b.status || 'active',
      notes: b.notes || '',
    });
    setOpenDialog(true);
  };

  const onSave = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    const payload = { ...form };
    if (!payload.department_id) payload.department_id = null;
    Object.keys(payload).forEach(k => { if (payload[k] === '') payload[k] = null; });
    try {
      if (editing) {
        await put(`/trainees/batches/${editing.batch_id}`, payload);
        toast.success('Batch updated');
      } else {
        delete payload.status;
        await post('/trainees/batches', payload);
        toast.success('Batch created');
      }
      setOpenDialog(false);
      await load();
    } catch (e) {
      const detail = e.response?.data?.detail || 'Save failed';
      if (!editing && e.response?.status === 409 && String(detail).includes('confirm to create')) {
        if (window.confirm(`${detail}\n\nCreate this batch anyway?`)) {
          try {
            await post('/trainees/batches', { ...payload, allow_duplicate_name: true });
            toast.success('Batch created');
            setOpenDialog(false);
            await load();
          } catch (e2) {
            toast.error(e2.response?.data?.detail || 'Save failed');
          }
        }
        return;
      }
      toast.error(detail);
    }
  };

  const onDelete = async () => {
    if (!confirmDelete) return;
    try {
      await del(`/trainees/batches/${confirmDelete.batch_id}`);
      toast.success('Batch deleted');
      setConfirmDelete(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  // Department filter applied client-side — cheap, even at 100s of batches.
  const visibleBatches = filterDept === ALL_DEPTS
    ? batches
    : batches.filter(b => b.department_id === filterDept);

  // Compute the placeholder auto-code so trainers see what the system
  // will generate before they save (e.g. "ROB-B7").
  const previewAutoCode = (() => {
    if (form.code) return null;
    const d = departments.find(x => x.department_id === form.department_id);
    if (!d?.code) return null;
    const prefix = `${d.code}-B`;
    const used = batches
      .map(b => b.code || '')
      .filter(c => c.startsWith(prefix))
      .map(c => parseInt(c.slice(prefix.length), 10))
      .filter(n => Number.isFinite(n));
    const next = (used.length ? Math.max(...used) : 0) + 1;
    return `${prefix}${next}`;
  })();

  return (
    <div className="space-y-4" data-testid="batches-tab">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          <p className="text-sm text-muted-foreground">{visibleBatches.length} batch{visibleBatches.length === 1 ? '' : 'es'}</p>
          {departments.length > 0 && (
            <div className="flex items-center gap-1">
              <Label className="text-xs text-muted-foreground">Department:</Label>
              <Select value={filterDept} onValueChange={setFilterDept}>
                <SelectTrigger className="h-8 w-48" data-testid="batches-dept-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_DEPTS}>All departments</SelectItem>
                  {departments.filter(d => d.is_active).map(d => (
                    <SelectItem key={d.department_id} value={d.department_id}>
                      {d.code} — {d.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        {canCreate && (
          <Button onClick={openCreate} data-testid="create-batch-btn">
            <Plus className="w-4 h-4 mr-2" /> New Batch
          </Button>
        )}
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : visibleBatches.length === 0 ? (
        <Card className="p-12 text-center" data-testid="batches-empty-state">
          <Boxes className="w-12 h-12 mx-auto text-muted-foreground/40 mb-3" />
          <h3 className="font-display text-lg font-semibold">
            {batches.length === 0 ? 'No batches yet' : 'No batches in this department'}
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            {batches.length === 0
              ? 'Create your first batch to start adding trainees.'
              : 'Try a different department, or create a batch for this one.'}
          </p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="batches-grid">
          {visibleBatches.map(b => (
            <Card
              key={b.batch_id}
              className="p-5 hover:shadow-md hover:border-foreground/30 transition-all cursor-pointer relative group"
              onClick={() => navigate(`/trainees/batches/${b.batch_id}`)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  navigate(`/trainees/batches/${b.batch_id}`);
                }
              }}
              data-testid={`batch-card-${b.batch_id}`}
            >
              <ChevronRight
                className="w-4 h-4 absolute top-5 right-5 text-muted-foreground/40 group-hover:text-foreground transition-colors"
                aria-hidden="true"
              />
              <div className="flex justify-between items-start mb-3 pr-6">
                <div>
                  <h3 className="font-display text-lg font-semibold">{b.name}</h3>
                  {b.code && <p className="text-xs text-muted-foreground">{b.code}</p>}
                </div>
                <Badge variant={(b.effective_status || b.status) === 'active' ? 'default' : 'secondary'}>
                  {b.effective_status || b.status}
                </Badge>
              </div>
              {b.department && <p className="text-sm text-muted-foreground">{b.department}</p>}
              <div className="flex items-baseline gap-2 mt-3">
                <span className="font-display tabular-nums text-2xl font-bold">{b.trainee_count}</span>
                <span className="text-xs text-muted-foreground">trainees</span>
              </div>
              {(canEdit || canDelete) && (
                <div className="flex gap-2 mt-4">
                  {canEdit && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={(e) => { e.stopPropagation(); openEdit(b); }}
                    data-testid={`edit-batch-${b.batch_id}`}
                  >
                    <Pencil className="w-3 h-3 mr-1" /> Edit
                  </Button>
                  )}
                  {canDelete && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={(e) => { e.stopPropagation(); setConfirmDelete(b); }}
                    data-testid={`delete-batch-${b.batch_id}`}
                  >
                    <Trash2 className="w-3 h-3 mr-1" /> Delete
                  </Button>
                  )}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      <Dialog open={openDialog} onOpenChange={setOpenDialog}>
        <DialogContent data-testid="batch-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Batch' : 'New Batch'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Name *</Label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                     placeholder="BATCH-9" data-testid="batch-input-name" />
            </div>
            <div>
              <Label>Department</Label>
              <Select
                value={form.department_id || NO_DEPT}
                onValueChange={v => setForm({ ...form, department_id: v === NO_DEPT ? '' : v })}
              >
                <SelectTrigger data-testid="batch-input-department">
                  <SelectValue placeholder="No department" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_DEPT}>— None —</SelectItem>
                  {departments.filter(d => d.is_active || d.department_id === form.department_id).map(d => (
                    <SelectItem key={d.department_id} value={d.department_id}>
                      {d.code} — {d.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {departments.length === 0 && (
                <p className="text-[11px] text-muted-foreground mt-1">
                  No departments configured yet. Add one from <strong>Settings → Departments</strong> to enable
                  auto-coded batch numbers.
                </p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Code</Label>
                <Input value={form.code} onChange={e => setForm({ ...form, code: e.target.value })}
                       placeholder={previewAutoCode || 'B9'} data-testid="batch-input-code" />
                {previewAutoCode && !form.code && (
                  <p className="text-[11px] text-muted-foreground mt-1">
                    Auto-generated as <code className="text-[11px]">{previewAutoCode}</code> if left blank.
                  </p>
                )}
              </div>
              {editing && (
                <div>
                  <Label>Status</Label>
                  <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
                    <SelectTrigger data-testid="batch-input-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                      <SelectItem value="archived">Archived</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Start Date</Label>
                <Input type="date" value={form.start_date || ''}
                       onChange={e => setForm({ ...form, start_date: e.target.value })}
                       data-testid="batch-input-start-date" />
              </div>
              <div>
                <Label>End Date</Label>
                <Input type="date" value={form.end_date || ''}
                       onChange={e => setForm({ ...form, end_date: e.target.value })}
                       data-testid="batch-input-end-date" />
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={form.description} rows={2}
                        onChange={e => setForm({ ...form, description: e.target.value })}
                        data-testid="batch-input-description" />
            </div>
            <div>
              <Label>Internal Notes</Label>
              <Textarea value={form.notes} rows={2}
                        placeholder="Trainer-only context (not shown to trainees)"
                        onChange={e => setForm({ ...form, notes: e.target.value })}
                        data-testid="batch-input-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenDialog(false)}>Cancel</Button>
            <Button onClick={onSave} data-testid="batch-save-btn">
              {editing ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete batch?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove <strong>{confirmDelete?.name}</strong>.
              Batches with active trainees cannot be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={onDelete} data-testid="confirm-delete-batch-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
